import type {
  AutomationHealthStatus,
  SystemOpsAIInventorySummary,
  SystemOpsAutomationSummary,
} from "@/types/systemOps";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireRecord(value: unknown, label: string): UnknownRecord {
  if (!isRecord(value)) throw new Error(`${label} is not an object`);
  return value;
}

function requireExact(value: unknown, expected: unknown, label: string): void {
  if (value !== expected)
    throw new Error(`${label} does not match its contract`);
}

function requireArray(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`${label} is not an array`);
  return value;
}

function requireSchemaVersion(value: unknown): number {
  if (!Number.isInteger(value) || (value as number) < 1) {
    throw new Error("schema_version is not a positive integer");
  }
  return value as number;
}

/**
 * Deliberately selects only the fields the landing page is allowed to show.
 * Capability descriptors, IDs, tool names, and future additive fields stay in
 * the dedicated AI inventory instead of leaking through this summary.
 */
export function adaptAIInventorySummary(
  payload: unknown,
): SystemOpsAIInventorySummary {
  const record = requireRecord(payload, "AI capability response");
  const capabilities = requireArray(record.capabilities, "capabilities");
  requireExact(
    record.generated_from,
    "app.ai.capability_registry",
    "generated_from",
  );
  requireExact(record.scope, "backend_llm_capabilities", "scope");
  requireExact(record.runtime_state_included, false, "runtime_state_included");

  return {
    catalogState: capabilities.length === 0 ? "empty" : "available",
    schemaVersion: requireSchemaVersion(record.schema_version),
    // The capability endpoint explicitly excludes runtime state. Never derive
    // health from flags, configured models, channels, or registry presence.
    runtimeStatus: "unavailable",
  };
}

const HEALTH_WEIGHT: Record<AutomationHealthStatus, number> = {
  healthy: 1,
  unknown: 2,
  warning: 3,
  critical: 4,
};

function explicitHealthStatus(value: unknown): AutomationHealthStatus | null {
  if (typeof value !== "string") return null;
  if (value === "healthy" || value === "warning" || value === "critical") {
    return value;
  }
  if (value === "unknown" || value === "disabled") return "unknown";
  return null;
}

/**
 * Selects a content-free aggregate from the existing operations endpoint.
 * It ignores IDs, run counts, details, errors, configuration reasons, and
 * latest-run payloads. Health comes only from explicit health.status rows.
 */
export function adaptAutomationSummary(
  payload: unknown,
): SystemOpsAutomationSummary {
  const record = requireRecord(payload, "operations response");
  const automations = requireArray(record.automations, "automations");
  requireExact(
    record.generated_from,
    "app.operations.automation_registry",
    "generated_from",
  );
  const statuses = automations.flatMap((automation) => {
    if (!isRecord(automation) || !isRecord(automation.health)) return [];
    const status = explicitHealthStatus(automation.health.status);
    return status ? [status] : [];
  });

  const healthStatus = statuses.reduce<AutomationHealthStatus>(
    (worst, status) =>
      HEALTH_WEIGHT[status] > HEALTH_WEIGHT[worst] ? status : worst,
    statuses[0] ?? "unknown",
  );

  return {
    catalogState: automations.length === 0 ? "empty" : "available",
    healthEvidence: statuses.length > 0 ? "available" : "unavailable",
    healthStatus,
  };
}
